import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Search as SearchIcon,
  Sort as SortIcon,
  Storefront as StorefrontIcon,
  Warehouse as WarehouseIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL, API_ENDPOINTS } from '../../config/api';
import EditItemDialog from './EditItemDialog';
import StorageItemView, { type ItemCartTransferMode } from './StorageItemView';

interface StorageItem {
  id: string;
  name: string;
  description: string;
  category: 'business' | 'office' | 'exhibition' | 'souvenirs' | 'work_tools';
  quantity: number;
  storeQuantity?: number;
  minQuantity: number;
  unit: string;
  location: string;
  supplier: string;
  barkodas?: string;
  prekesKodas?: string;
  fabisKodas?: string;
  savikaina?: number;
  pardavimoKaina?: number;
  creator: {
    firstName: string;
    lastName: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface SouvenirItemsProps {
  refreshTrigger: number;
  onAddToCart: (item: StorageItem, quantity: number, transferSource?: ItemCartTransferMode) => void;
  viewMode: 'cards' | 'list';
  cartTransferMode?: ItemCartTransferMode;
  onCartTransferModeChange?: (mode: ItemCartTransferMode) => void;
}

const SouvenirItems: React.FC<SouvenirItemsProps> = ({
  refreshTrigger,
  onAddToCart,
  viewMode,
  cartTransferMode = 'warehouse',
  onCartTransferModeChange
}) => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canTransfer = hasPermission('storage.edit');
  const [items, setItems] = useState<StorageItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [supplierFilter, setSupplierFilter] = useState<string>('');
  const [missingPrekesKodasOnly, setMissingPrekesKodasOnly] = useState(false);
  const [inStoreOnly, setInStoreOnly] = useState(false);
  const [allSuppliers, setAllSuppliers] = useState<string[]>([]);
  const [selectedQuantities, setSelectedQuantities] = useState<{ [key: string]: number }>({});
  const [editItemOpen, setEditItemOpen] = useState(false);
  const [itemToEdit, setItemToEdit] = useState<StorageItem | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<StorageItem | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchSuppliers = useCallback(async () => {
    try {
      setLoadingSuppliers(true);
      setError(null);
      const response = await fetch(
        `${API_BASE_URL}/api${API_ENDPOINTS.STORAGE_SUPPLIERS_BY_CATEGORY('souvenirs')}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.success && Array.isArray(data.suppliers)) {
        setAllSuppliers(data.suppliers);
      } else {
        throw new Error(data.message || 'Failed to fetch suppliers');
      }
    } catch (err) {
      console.error('Error fetching souvenir suppliers:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch suppliers');
    } finally {
      setLoadingSuppliers(false);
    }
  }, []);

  const fetchItems = useCallback(async () => {
    if (!supplierFilter) {
      setItems([]);
      return;
    }

    try {
      setLoadingItems(true);
      setError(null);

      const params = new URLSearchParams({
        sortBy,
        sortOrder
      });
      if (supplierFilter !== 'all') {
        params.set('supplier', supplierFilter);
      }

      const response = await fetch(
        `${API_BASE_URL}/api${API_ENDPOINTS.STORAGE_ITEMS_BY_CATEGORY('souvenirs')}?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setItems(data.items);
      } else {
        throw new Error(data.message || 'Failed to fetch items');
      }
    } catch (err) {
      console.error('Error fetching souvenir items:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch items');
    } finally {
      setLoadingItems(false);
    }
  }, [supplierFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers, refreshTrigger]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems, refreshTrigger]);

  useEffect(() => {
    if (cartTransferMode === 'store') {
      setInStoreOnly(true);
    }
  }, [cartTransferMode]);

  const handleSort = (field: string) => {
    if (!supplierFilter) return;
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
  };

  const handleQuantityChange = (itemId: string, quantity: number) => {
    const item = items.find(i => i.id === itemId);
    const maxQty = cartTransferMode === 'store'
      ? (item?.storeQuantity ?? 0)
      : (item?.quantity ?? 1);
    setSelectedQuantities(prev => ({
      ...prev,
      [itemId]: Math.max(1, Math.min(quantity, maxQty || 1))
    }));
  };

  const handleAddToCart = (item: StorageItem) => {
    const quantity = selectedQuantities[item.id] || 1;
    onAddToCart(item, quantity, cartTransferMode);
    setSelectedQuantities(prev => {
      const newState = { ...prev };
      delete newState[item.id];
      return newState;
    });
  };

  const handleEditItem = (item: StorageItem) => {
    setItemToEdit(item);
    setEditItemOpen(true);
  };

  const handleItemUpdated = () => {
    fetchItems();
    fetchSuppliers();
  };

  const handleDeleteItem = (item: StorageItem) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const handleTransferModeChange = (_e: React.MouseEvent<HTMLElement>, value: ItemCartTransferMode | null) => {
    if (!value || !onCartTransferModeChange) return;
    if (value === 'store') {
      setInStoreOnly(true);
    }
    onCartTransferModeChange(value);
  };

  const confirmDeleteItem = async () => {
    if (!itemToDelete) return;

    try {
      setDeleteLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/storage/items/${itemToDelete.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to delete item');
      }

      await fetchItems();
      await fetchSuppliers();
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    } catch (err) {
      console.error('Error deleting item:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete item');
    } finally {
      setDeleteLoading(false);
    }
  };

  const supplierSelected = Boolean(supplierFilter);
  const filtersDisabled = !supplierSelected || loadingItems;

  const searchLower = searchTerm.toLowerCase();
  const filteredItems = items.filter((item) => {
    const matchesSearch =
      item.name.toLowerCase().includes(searchLower) ||
      item.description?.toLowerCase().includes(searchLower) ||
      item.location?.toLowerCase().includes(searchLower) ||
      item.barkodas?.toLowerCase().includes(searchLower) ||
      item.prekesKodas?.toLowerCase().includes(searchLower) ||
      item.fabisKodas?.toLowerCase().includes(searchLower);
    const matchesPrekesKodasFilter =
      !missingPrekesKodasOnly || !item.prekesKodas?.trim();
    const matchesInStoreFilter =
      !inStoreOnly || (item.storeQuantity ?? 0) > 0;
    return matchesSearch && matchesPrekesKodasFilter && matchesInStoreFilter;
  });

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {canTransfer && onCartTransferModeChange && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {cartTransferMode === 'store'
            ? t('storage.souvenirs.returnFromStoreHint')
            : t('storage.souvenirs.transferToStoreHint')}
        </Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 220 }} required>
          <InputLabel>Tiekėjas *</InputLabel>
          <Select
            value={supplierFilter === 'all' ? '' : supplierFilter}
            label="Tiekėjas *"
            onChange={(e) => setSupplierFilter(e.target.value)}
            disabled={loadingSuppliers}
          >
            <MenuItem value="">
              <em>{t('storage.souvenirs.selectSupplier')}</em>
            </MenuItem>
            {allSuppliers.map((supplier) => (
              <MenuItem key={supplier} value={supplier}>
                {supplier}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant={supplierFilter === 'all' ? 'contained' : 'outlined'}
          size="small"
          onClick={() => setSupplierFilter('all')}
          disabled={loadingSuppliers || loadingItems}
          sx={
            supplierFilter === 'all'
              ? { backgroundColor: '#7b1fa2', '&:hover': { backgroundColor: '#6a1b9a' } }
              : { borderColor: '#7b1fa2', color: '#7b1fa2' }
          }
        >
          {t('storage.souvenirs.allSuppliers')}
        </Button>

        {loadingSuppliers && <CircularProgress size={24} />}

        {canTransfer && onCartTransferModeChange && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {t('storage.souvenirs.transferModeLabel')}
            </Typography>
            <ToggleButtonGroup
              value={cartTransferMode}
              exclusive
              size="small"
              onChange={handleTransferModeChange}
            >
              <ToggleButton value="warehouse">
                <WarehouseIcon sx={{ mr: 0.5, fontSize: 18 }} />
                {t('storage.souvenirs.transferModeWarehouse')}
              </ToggleButton>
              <ToggleButton value="store">
                <StorefrontIcon sx={{ mr: 0.5, fontSize: 18 }} />
                {t('storage.souvenirs.transferModeStore')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        <TextField
          placeholder="Ieškoti pagal pavadinimą, aprašymą, vietą, barkodą ar prekės kodą..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={filtersDisabled}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
          }}
          sx={{ flexGrow: 1, minWidth: 250 }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={inStoreOnly}
              onChange={(e) => setInStoreOnly(e.target.checked)}
              disabled={filtersDisabled}
            />
          }
          label={t('storage.souvenirs.inStoreOnly')}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={missingPrekesKodasOnly}
              onChange={(e) => setMissingPrekesKodasOnly(e.target.checked)}
              disabled={filtersDisabled}
            />
          }
          label={t('storage.souvenirs.missingPrekesKodas')}
        />

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant={sortBy === 'name' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => handleSort('name')}
            startIcon={<SortIcon />}
            disabled={filtersDisabled}
          >
            Pavadinimas {sortBy === 'name' && (sortOrder === 'ASC' ? '↑' : '↓')}
          </Button>
          <Button
            variant={sortBy === 'quantity' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => handleSort('quantity')}
            startIcon={<SortIcon />}
            disabled={filtersDisabled}
          >
            Kiekis {sortBy === 'quantity' && (sortOrder === 'ASC' ? '↑' : '↓')}
          </Button>
        </Box>
      </Box>

      {!supplierSelected ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" color="text.secondary">
            {t('storage.souvenirs.selectSupplierPrompt')}
          </Typography>
        </Box>
      ) : loadingItems ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : (
        <StorageItemView
          items={filteredItems}
          viewMode={viewMode}
          selectedQuantities={selectedQuantities}
          onQuantityChange={handleQuantityChange}
          onAddToCart={handleAddToCart}
          onEdit={handleEditItem}
          onDelete={handleDeleteItem}
          canEdit={hasPermission('storage.edit')}
          showStoreQuantity
          cartTransferMode={cartTransferMode}
          emptyMessage={
            inStoreOnly
              ? t('storage.souvenirs.noInStore')
              : missingPrekesKodasOnly
              ? t('storage.souvenirs.noMissingPrekesKodas')
              : supplierFilter === 'all'
              ? 'Suvenyrų nerasta'
              : t('storage.souvenirs.noItemsForSupplier')
          }
        />
      )}

      <EditItemDialog
        open={editItemOpen}
        onClose={() => setEditItemOpen(false)}
        item={itemToEdit}
        onItemUpdated={handleItemUpdated}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Patvirtinti ištrynimą</DialogTitle>
        <DialogContent>
          <Typography>
            Ar tikrai norite ištrinti prekę "{itemToDelete?.name}"?
            Šis veiksmas negrįžtamas.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            Atšaukti
          </Button>
          <Button
            onClick={confirmDeleteItem}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} /> : 'Ištrinti'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SouvenirItems;
